import React, { useState } from "react";
import { 
  Typography, 
  Box, 
  Stack, 
  Grid, 
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch
} from "@mui/material";
import { 
  Error,
  Settings,
  Person,
  Payment
} from "@mui/icons-material";

import AutobiaStepper from "../components/ProcessFlow/AutobiaStepper";
import AutobiaFilterSection from "../components/Inputs/AutobiaFilterSection";
import ComponentDemo from "../components/ComponentDemo/ComponentDemo";

const ProcessFlow = () => {
  // Demo states
  const [currentStep, setCurrentStep] = useState("step2");
  const [orientation, setOrientation] = useState("horizontal");
  const [variant, setVariant] = useState("default");
  const [showLabels, setShowLabels] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    status: "",
    priority: ""
  });

  // Demo data
  const basicSteps = [
    { id: "step1", label: "Personal Information", status: "completed" },
    { id: "step2", label: "Payment Details", status: "active" },
    { id: "step3", label: "Confirmation", status: "pending" },
  ];

  const advancedSteps = [
    { 
      id: "step1", 
      label: "Account Setup", 
      status: "completed",
      description: "Basic account information",
      icon: <Person />
    },
    { 
      id: "step2", 
      label: "Payment Configuration", 
      status: "active",
      description: "Set up payment methods",
      icon: <Payment />
    },
    { 
      id: "step3", 
      label: "Verification", 
      status: "error",
      description: "Identity verification failed",
      icon: <Error />
    },
    { 
      id: "step4", 
      label: "Final Setup", 
      status: "pending",
      optional: true,
      description: "Complete profile setup",
      icon: <Settings />
    },
  ];

  const filterConfig = [
    {
      key: "search",
      type: "text",
      title: "Search Items",
      placeholder: "Search for items...",
    },
    {
      key: "category",
      type: "select",
      title: "Category",
      placeholder: "Select category",
      options: [
        { value: "electronics", label: "Electronics", name_en: "Electronics", name_ar: "الإلكترونيات" },
        { value: "clothing", label: "Clothing", name_en: "Clothing", name_ar: "الملابس" },
        { value: "books", label: "Books", name_en: "Books", name_ar: "الكتب" },
      ]
    },
    {
      key: "status",
      type: "select",
      title: "Status",
      placeholder: "Select status",
      options: [
        { value: "active", label: "Active", name_en: "Active", name_ar: "نشط" },
        { value: "inactive", label: "Inactive", name_en: "Inactive", name_ar: "غير نشط" },
        { value: "pending", label: "Pending", name_en: "Pending", name_ar: "معلق" },
      ]
    },
    {
      key: "priority",
      type: "select",
      title: "Priority",
      placeholder: "Select priority",
      options: [
        { value: "high", label: "High", name_en: "High", name_ar: "عالي" },
        { value: "medium", label: "Medium", name_en: "Medium", name_ar: "متوسط" },
        { value: "low", label: "Low", name_en: "Low", name_ar: "منخفض" },
      ]
    },
  ];

  const codeExamples = {
    basicStepper: `import AutobiaStepper from './components/ProcessFlow/AutobiaStepper';

// Basic Stepper
const steps = [
  { id: "step1", label: "Personal Information", status: "completed" },
  { id: "step2", label: "Payment Details", status: "active" },
  { id: "step3", label: "Confirmation", status: "pending" },
];

<AutobiaStepper
  currentStep="step2"
  steps={steps}
  orientation="horizontal"
/>`,

    advancedStepper: `// Advanced Stepper with Icons and Descriptions
const advancedSteps = [
  { 
    id: "step1", 
    label: "Account Setup", 
    status: "completed",
    description: "Basic account information",
    icon: <Person />
  },
  { 
    id: "step2", 
    label: "Payment Configuration", 
    status: "error",
    description: "Identity verification failed"
  },
  { 
    id: "step3", 
    label: "Final Setup", 
    status: "pending",
    optional: true
  },
];

<AutobiaStepper
  currentStep="step2"
  steps={advancedSteps}
  variant="default"
  activeColor="secondary"
  completedColor="success"
/>`,

    filterSection: `import AutobiaFilterSection from './components/Inputs/AutobiaFilterSection';

const filterConfig = [
  {
    key: "search",
    type: "text",
    title: "Search Items",
    placeholder: "Search for items...",
  },
  {
    key: "category",
    type: "select",
    title: "Category",
    options: [
      { value: "electronics", name_en: "Electronics" },
      { value: "clothing", name_en: "Clothing" },
    ]
  }
];

<AutobiaFilterSection
  filters={filters}
  onFilterChange={setFilters}
  filterConfig={filterConfig}
  showActiveFilters={true}
/>`
  };

  const handleStepChange = (direction) => {
    const stepIds = basicSteps.map(step => step.id);
    const currentIndex = stepIds.indexOf(currentStep);
    
    if (direction === "next" && currentIndex < stepIds.length - 1) {
      setCurrentStep(stepIds[currentIndex + 1]);
    } else if (direction === "prev" && currentIndex > 0) {
      setCurrentStep(stepIds[currentIndex - 1]);
    }
  };

  return (
    <Box sx={{ padding: "2rem" }}>
      <Typography variant="h3" fontWeight={700} gutterBottom color="secondary.main">
        🔄 Process Flow Components
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: "800px" }}>
        Components for managing multi-step processes, workflows, and complex filtering interfaces.
        Perfect for onboarding flows, forms, and advanced search functionality.
      </Typography>

      {/* Stepper Component */}
      <ComponentDemo
        title="Enhanced Stepper Component"
        description="Customizable stepper with support for different orientations, variants, error states, and optional steps."
        component={
          <Box>
            <Stack spacing={4}>
              {/* Basic Stepper */}
              <Box>
                <Typography variant="h6" gutterBottom>Basic Stepper</Typography>
                <AutobiaStepper
                  currentStep={currentStep}
                  steps={basicSteps}
                  orientation={orientation}
                  variant={variant}
                  showLabels={showLabels}
                />
                
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => handleStepChange("prev")}
                    disabled={currentStep === "step1"}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => handleStepChange("next")}
                    disabled={currentStep === "step3"}
                  >
                    Next Step
                  </Button>
                </Stack>
              </Box>

              {/* Advanced Stepper with Error States */}
              <Box>
                <Typography variant="h6" gutterBottom>Advanced Stepper with Status States</Typography>
                <AutobiaStepper
                  currentStep="step2"
                  steps={advancedSteps}
                  orientation={orientation}
                  variant={variant}
                  showLabels={showLabels}
                />
              </Box>
            </Stack>

            {/* Interactive Controls */}
            <Card sx={{ mt: 3, p: 2, bgcolor: "background.default" }}>
              <Typography variant="h6" gutterBottom>Interactive Demo</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Orientation</FormLabel>
                    <RadioGroup
                      row
                      value={orientation}
                      onChange={(e) => setOrientation(e.target.value)}
                    >
                      <FormControlLabel value="horizontal" control={<Radio />} label="Horizontal" />
                      <FormControlLabel value="vertical" control={<Radio />} label="Vertical" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Variant</FormLabel>
                    <RadioGroup
                      row
                      value={variant}
                      onChange={(e) => setVariant(e.target.value)}
                    >
                      <FormControlLabel value="default" control={<Radio />} label="Default" />
                      <FormControlLabel value="compact" control={<Radio />} label="Compact" />
                      <FormControlLabel value="minimal" control={<Radio />} label="Minimal" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={showLabels} 
                        onChange={(e) => setShowLabels(e.target.checked)}
                      />
                    }
                    label="Show Labels"
                  />
                </Grid>
              </Grid>
            </Card>
          </Box>
        }
        usage={codeExamples.basicStepper}
      />

      {/* Filter Section Component */}
      <ComponentDemo
        title="Advanced Filter Section"
        description="Comprehensive filtering component with active filter chips, multiple input types, and responsive design."
        component={
          <Box>
            <AutobiaFilterSection
              filters={filters}
              onFilterChange={setFilters}
              filterConfig={filterConfig}
              showActiveFilters={true}
              variant="default"
            />

            <Card sx={{ mt: 3, p: 2, bgcolor: "background.paper" }}>
              <Typography variant="h6" gutterBottom>Current Filter State</Typography>
              <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(filters, null, 2)}
              </Typography>
            </Card>
          </Box>
        }
        usage={codeExamples.filterSection}
      />

      {/* Stepper Variants */}
      <ComponentDemo
        title="Stepper Variants and Customization"
        description="Different variants, sizes, and color schemes for various use cases."
        component={
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Compact Variant</Typography>
              <AutobiaStepper
                currentStep="step2"
                steps={basicSteps}
                variant="compact"
                activeColor="primary"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Minimal Variant</Typography>
              <AutobiaStepper
                currentStep="step2"
                steps={basicSteps}
                variant="minimal"
                activeColor="success"
                completedColor="primary"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Vertical Orientation</Typography>
              <AutobiaStepper
                currentStep="step2"
                steps={advancedSteps}
                orientation="vertical"
                activeColor="secondary"
              />
            </Grid>
          </Grid>
        }
        usage={codeExamples.advancedStepper}
      />

      {/* Usage Guidelines */}
      <Card sx={{ mt: 4, bgcolor: "gray.g25", border: '1px solid', borderColor: 'gray.g200' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="gray.g700">
            💡 Usage Guidelines
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="gray.g700" fontWeight={600}>Stepper Component</Typography>
              <Typography variant="body2" sx={{ mb: 2 }} color="gray.g600">
                • Use for multi-step processes<br/>
                • Show clear progress indication<br/>
                • Include error states for failed steps<br/>
                • Mark optional steps clearly<br/>
                • Use vertical orientation for mobile
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="gray.g700" fontWeight={600}>Filter Section</Typography>
              <Typography variant="body2" color="gray.g600">
                • Group related filters together<br/>
                • Show active filters with chips<br/>
                • Make filters easy to clear<br/>
                • Use responsive grid layout<br/>
                • Support keyboard navigation
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProcessFlow;